import { useEffect, useRef, useState } from "react";

import { reverse } from "@reactivated";
import type { SpecificationsBlock } from "@reactivated";

import type { SKU } from "../engine/schema";

/**
 * A SKU's specifications, as the reactivated `SpecificationsBlock` payload — the
 * same shape the PDP renders, served by `cms-api:product-specs`. `null` when the
 * product has no live page or no specifications block.
 */
type ProductSpecs = SpecificationsBlock | null;

export type SpecsFetchState =
    | { status: "idle" }
    | { status: "loading" }
    | { status: "error" }
    | { status: "ready"; specs: ProductSpecs };

/**
 * Lazily fetch a SKU's PDP specifications. Idle (no request) while `sku` is
 * null, so the catalog payload stays lean and specs load only when a details
 * modal opens. Re-fetches when `sku` or `locale` changes; aborts in-flight
 * requests on change/unmount.
 */
export const useProductSpecs = (
    sku: SKU | null,
    locale: string,
): SpecsFetchState => {
    const [fetchState, setFetchState] = useState<SpecsFetchState>({
        status: "idle",
    });
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        abortControllerRef.current?.abort();

        if (!sku) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- reset to idle when no SKU is selected (modal closed)
            setFetchState({ status: "idle" });
            return;
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        setFetchState({ status: "loading" });

        const url = reverse("cms-api:product-specs");
        const params = new URLSearchParams({ locale, sku });

        fetch(`${url}?${params.toString()}`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                return response.json();
            })
            .then((data: { specs: ProductSpecs }) => {
                if (controller.signal.aborted) return;
                // `specs` is null when the product has no spec block — a normal
                // empty result, rendered as no panels rather than an error.
                setFetchState({ status: "ready", specs: data.specs });
            })
            /* v8 ignore start -- @preserve */
            .catch((error) => {
                if (controller.signal.aborted) return;
                console.error("Failed to fetch product specs:", error);
                setFetchState({ status: "error" });
            });
        /* v8 ignore stop -- @preserve */

        return () => {
            controller.abort();
        };
    }, [sku, locale]);

    return fetchState;
};
